import React, { useEffect, useId, useRef, useState } from "react";
import { AlertTriangle, Loader2, X } from "lucide-react";

const MIN_REASON_LENGTH = 3;

/**
 * Confirmation dialog for privileged admin actions.
 *
 * Exists because several admin actions (suspending an account, blocking a
 * customer, disabling a route) are destructive, effectively irreversible from
 * the customer's point of view, and were previously one un-guarded click. When
 * `requireReason` is set the confirm button stays disabled until a reason is
 * typed - the backend enforces the same rule and returns 422 without one, so
 * this only makes the requirement visible earlier.
 */
export function ConfirmActionModal({
  open,
  title,
  description,
  confirmLabel = "Confirm",
  destructive = false,
  requireReason = false,
  reasonLabel = "Reason",
  reasonPlaceholder = "Why are you doing this? Recorded in the audit log.",
  error = null,
  isSubmitting = false,
  onConfirm,
  onCancel,
}) {
  const [reason, setReason] = useState("");
  const descriptionId = useId();
  const reasonInputRef = useRef(null);
  const cancelButtonRef = useRef(null);

  // Never carry a reason across two different decisions.
  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  // Move focus into the dialog on open. Without this, focus stays on the
  // trigger behind the overlay and a keyboard user tabs through the page
  // underneath a modal they cannot see they are outside of. Focus the reason
  // box when there is one - it is the only thing standing between the admin
  // and the action - otherwise the safe (Cancel) button.
  useEffect(() => {
    if (!open) return;
    const target = requireReason ? reasonInputRef.current : cancelButtonRef.current;
    target?.focus();
  }, [open, requireReason]);

  // Escape cancels. Every other dialog on the web does this; a destructive
  // confirmation that traps you until you click is worse, not safer.
  useEffect(() => {
    if (!open) return undefined;
    function onKeyDown(event) {
      if (event.key === "Escape" && !isSubmitting) {
        event.stopPropagation();
        onCancel();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, isSubmitting, onCancel]);

  if (!open) return null;

  const reasonMissing = requireReason && reason.trim().length < MIN_REASON_LENGTH;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        aria-describedby={description ? descriptionId : undefined}
        className="bg-white rounded-3xl max-w-md w-full p-6 space-y-5 shadow-2xl border border-slate-100"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            {destructive && (
              <div className="w-10 h-10 rounded-2xl bg-red-50 text-red-500 grid place-items-center shrink-0">
                <AlertTriangle size={18} />
              </div>
            )}
            <div>
              <h3 className="font-semibold text-[#0A2540]">{title}</h3>
              {description && (
                <p
                  id={descriptionId}
                  className="text-sm text-[#0A2540]/60 mt-1 leading-relaxed"
                >
                  {description}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="p-2 rounded-xl text-slate-400 hover:bg-slate-50 transition disabled:opacity-50"
            aria-label="Cancel"
          >
            <X size={18} />
          </button>
        </div>

        {requireReason && (
          <div>
            <label className="block text-[9px] uppercase tracking-[0.18em] font-semibold text-[#0A4730]/70 mb-2">
              {reasonLabel} <span className="text-red-500">*</span>
            </label>
            <textarea
              ref={reasonInputRef}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={3}
              placeholder={reasonPlaceholder}
              aria-required="true"
              aria-invalid={reasonMissing}
              className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm text-[#0A2540] focus:outline-none focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5"
            />
            {/* Say why the button is greyed out. A disabled control with no
                explanation reads as a broken page, not as a requirement. */}
            <p className="text-xs text-slate-400 mt-1.5">
              {reasonMissing
                ? `Enter at least ${MIN_REASON_LENGTH} characters to continue.`
                : "Stored in the admin audit log against your account."}
            </p>
          </div>
        )}

        {error && (
          <div className="rounded-2xl bg-red-50 border border-red-100 p-3 text-sm text-red-600">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="h-11 px-5 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-[#0A4730] hover:bg-slate-50 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim())}
            disabled={isSubmitting || reasonMissing}
            className={`h-11 px-5 rounded-xl text-xs font-semibold text-white transition disabled:opacity-50 inline-flex items-center gap-2 ${
              destructive ? "bg-red-500 hover:bg-red-600" : "bg-[#0A4730] hover:bg-[#0A4730]/90"
            }`}
          >
            {isSubmitting && <Loader2 size={14} className="animate-spin" />}
            {isSubmitting ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
